import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core'
import { RequestedDateTimeWithAvailability, SchedulingRuleGroupSettings, WorkingDays } from '@mmx/shared'
import { first } from 'lodash'
import moment from 'moment'
import { BehaviorSubject, combineLatest, combineLatestWith, filter, map, Observable, startWith } from 'rxjs'

import { TimeSlotPickerCustomComponent } from '../time-slot-picker-custom'

export interface TimeSlotPickerDateRange {
  from: string
  to: string
}

export interface TimeSlotAvailableTimesData {
  timeSlots: moment.Moment[]
  recommended?: moment.Moment[]
  soonest?: moment.Moment[]
}

export interface TimeSlotPickerOptions {
  rules?: SchedulingRuleGroupSettings
  dateRange: TimeSlotPickerDateRange
  minNumberOfTimeSlots: number
  maxNumberOfTimeSlots: number
  availableTimes: TimeSlotAvailableTimesData
  defaultAvailableTimes: TimeSlotAvailableTimesData
  shouldResetSelection: boolean
  timezone: string
  workingDays: WorkingDays
  selectedTimeSlots?: RequestedDateTimeWithAvailability[]
  readonly?: boolean
  showRecommended?: boolean
}

export interface SelectedTimeWithAvailability {
  time: moment.Moment
  isAvailable: boolean
}

@Component({
  selector: 'mmx-time-slot-picker',
  templateUrl: './time-slot-picker.component.html',
  styleUrls: ['./time-slot-picker.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class TimeSlotPickerComponent implements OnChanges {
  @ViewChild(TimeSlotPickerCustomComponent)
  public customPicker: TimeSlotPickerCustomComponent | undefined

  @Input()
  public options: TimeSlotPickerOptions | undefined

  @Input()
  public loading = false

  @Output()
  public confirm = new EventEmitter<moment.Moment[] | null>()

  @Output()
  public dateRangeChange = new EventEmitter<TimeSlotPickerDateRange>()

  private onChanges$ = new BehaviorSubject<SimpleChanges>({})
  public allSelectedTimeSlots$ = new BehaviorSubject<SelectedTimeWithAvailability[]>([])
  public confirmed$ = new BehaviorSubject<boolean>(false)

  public selectedTimeSlots$ = this.allSelectedTimeSlots$.pipe(
    map((slots) => slots.filter((s) => s.isAvailable).map((s) => s.time)),
  )

  get minNumberOfTimeSlots() {
    return this.options?.minNumberOfTimeSlots ?? 1
  }

  get maxNumberOfTimeSlots() {
    return this.options?.maxNumberOfTimeSlots ?? 3
  }

  public changes$ = combineLatest([
    this.allSelectedTimeSlots$,
    this.onChanges$,
  ])

  public isVisible$ = this.changes$.pipe(
    map(() => {
      if (this.loading || !this.options) {
        return true
      }

      return !!this.options.availableTimes?.timeSlots?.length
    }),
  )

  public hasAvailableSlots$ = this.changes$.pipe(
    filter(() => !!this.options && !this.loading),
    map(() => !!this.options?.availableTimes?.timeSlots?.length),
    startWith(true), // Show the picker before we request the available times
  )

  public nextRecommendedTimeSlot$ = this.changes$.pipe(
    filter(() => this.options?.showRecommended ?? true),
    map(([selectedTimeSlots]) => {
      if (!this.options?.defaultAvailableTimes.recommended?.length) {
        return
      }

      if (!selectedTimeSlots.length) {
        return this.options?.defaultAvailableTimes.recommended?.[0]
      }

      return this.options.defaultAvailableTimes.recommended.find((date) => this.isTimeSlotSelected(date) === false)
    }),
  )

  public soonestTimeSlot$ = this.changes$.pipe(
    combineLatestWith(this.nextRecommendedTimeSlot$),
    map(([, recommendedTimeSlot]) => {
      if (!this.options?.defaultAvailableTimes.soonest?.length) {
        return
      }

      const slots = this.options.defaultAvailableTimes.soonest.filter(date => this.isTimeSlotSelected(date) === false)
      const slot = first(slots)

      return slot?.isSame(recommendedTimeSlot) ? undefined : slot
    }),
  )

  public timeSlotsRequired$: Observable<number> = this.selectedTimeSlots$.pipe(
    map(selected => Math.max(this.minNumberOfTimeSlots - selected.length, 0)),
  )

  public timeSlotsRemaining$: Observable<number> = this.selectedTimeSlots$.pipe(
    map(selected => Math.max(this.maxNumberOfTimeSlots - selected.length, 0)),
  )

  public confirmDisabled$ = combineLatest([
    this.selectedTimeSlots$,
    this.confirmed$,
  ]).pipe(
    map(([selected, confirmed]) => {
      if (confirmed) {
        return true
      }

      return selected.length < this.minNumberOfTimeSlots || selected.length > this.maxNumberOfTimeSlots
    }),
  )

  public ngOnChanges(changes: SimpleChanges) {
    this.onChanges$.next(changes)

    if (!changes['options']?.currentValue) {
      return
    }

    if (this.options?.shouldResetSelection) {
      const times = (this.options.selectedTimeSlots || []).map((selectedTime) => ({
        time: moment.tz(`${selectedTime.date} ${selectedTime.time}`, this.options!.timezone),
        isAvailable: selectedTime.isAvailable,
      }))
      this.allSelectedTimeSlots$.next(times)

      this.customPicker?.reset()
    }
  }

  public onAdd(timeSlot: moment.Moment) {
    if (!this.canAdd()) {
      return
    }

    this.confirmed$.next(false)
    this.allSelectedTimeSlots$.next([
      ...this.allSelectedTimeSlots$.getValue(),
      {
        time: timeSlot,
        isAvailable: true,
      },
    ])

    this.emit()
  }

  public onRemove(timeSlot: moment.Moment) {
    this.confirmed$.next(false)
    this.allSelectedTimeSlots$.next(
      this.allSelectedTimeSlots$.getValue().filter((s) => this.isTimeSlotSameTime(s.time, timeSlot) === false),
    )

    this.emit()
  }

  private emit() {
    const selected = this.allSelectedTimeSlots$.getValue()
    this.confirm.emit(selected.map((s) => s.time))
  }

  private isTimeSlotSelected(date: moment.Moment): boolean {
    return !!this.allSelectedTimeSlots$.getValue().find((s) => this.isTimeSlotSameTime(s.time, date))
  }

  private isTimeSlotSameTime(timeSlotA: moment.Moment, timeSlotB: moment.Moment): boolean {
    return timeSlotA.diff(timeSlotB, 'minutes') === 0
  }

  private canAdd(): boolean {
    const selected = this.allSelectedTimeSlots$.getValue().length

    return selected < (this.options?.maxNumberOfTimeSlots || 5)
  }
}
